import React, { useState } from 'react';
import {
  CalendarDaysIcon,
  FolderOpenIcon,
} from '@heroicons/react/24/outline';
import { CheckCircleIcon as CheckCircleSolidIcon } from '@heroicons/react/24/solid';
import { RETAINER_TYPES } from '../../constants/mockBidData';

// ─────────────────────────────────────────────────────────────────────────────
// Project types — canonical list
// ─────────────────────────────────────────────────────────────────────────────
export const PROJECT_TYPES = [
  { id: 'advertising',         name: 'Advertising' },
  { id: 'awards-campaign',     name: 'Awards Campaign' },
  { id: 'crisis-response',     name: 'Crisis Response' },
  { id: 'marketing-materials', name: 'Marketing Materials' },
  { id: 'media-relations',     name: 'Media Relations' },
  { id: 'newsletter-program',  name: 'Newsletter Program' },
  { id: 'nominations',         name: 'Nominations' },
  { id: 'press-event',         name: 'Press Conference / Event' },
  { id: 'rebranding',          name: 'Rebranding / Positioning' },
  { id: 'seo',                 name: 'SEO' },
  { id: 'social-media',        name: 'Social Media' },
  { id: 'speaking',            name: 'Speaking' },
  { id: 'strategic-marketing', name: 'Strategic Marketing' },
  { id: 'thought-leadership',  name: 'Thought Leadership' },
  { id: 'webinars',            name: 'Webinars' },
  { id: 'website-development', name: 'Website Development' },
  { id: 'others',              name: 'Other' },
];

// ─────────────────────────────────────────────────────────────────────────────
// Retainer services — only shown for Retainer A and B
// ─────────────────────────────────────────────────────────────────────────────
const RETAINER_SERVICE_OPTIONS = [
  'Strategic Partnership',
  'Media Coverage',
  'Litigation (PR)',
  'Awards & Rankings',
  'Nominations',
  // 'Search Engine Optimize',
  // 'Social Media',
  // 'Speaking',  
  // 'Webinars',
  // 'Website',
];

const RETAINER_SERVICE_DEFAULTS = [
  'Strategic Partnership',
  'Media Coverage',
  'Litigation (PR)',
  'Awards & Rankings',
];

// Order is intentional: Strategic Partnership → Media Coverage → Litigation (PR) → Awards & Rankings

const RETAINERS_WITH_SERVICES = new Set(['retainer-a', 'retainer-b']);

const RETAINER_DESCRIPTIONS = {
  'retainer-a': 'Full-service PR — media relations, social media, strategic oversight',
  'retainer-b': 'Focused media relations and press outreach',
  'retainer-c': 'Digital-first social media and online presence',
};

const ENGAGEMENT_TYPES = [
  {
    id: 'retainer',
    label: 'Retainer',
    icon: CalendarDaysIcon,
    description: 'Ongoing monthly engagement with recurring scope and billing',
    color: 'purple',
  },
  {
    id: 'project',
    label: 'Project',
    icon: FolderOpenIcon,
    description: 'One-time scoped engagement with a defined deliverable',
    color: 'purple',
  },
];

// ─────────────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────────────

const Step1EngagementType = ({ wizardData, onSelectEngagementType }) => {
  const [engagementType, setEngagementType] = useState(
    wizardData.engagementType || null
  );
  const [selectedRetainer, setSelectedRetainer] = useState(
    wizardData.engagementType === 'retainer' ? wizardData.componentType : null
  );
  const [selectedProjects, setSelectedProjects] = useState(
    wizardData.selectedProjects || []
  );
  const [selectedRetainerServices, setSelectedRetainerServices] = useState(
    wizardData.selectedRetainerServices?.length
      ? wizardData.selectedRetainerServices
      : RETAINER_SERVICE_DEFAULTS
  );

  // ── Type toggle ───────────────────────────────────────────────────
  const handleTypeToggle = (type) => {
    if (type === engagementType) return;
    setEngagementType(type);
    setSelectedRetainer(null);
    setSelectedProjects([]);
    setSelectedRetainerServices(RETAINER_SERVICE_DEFAULTS);
  };

  // ── Retainer: single select + notify parent ───────────────────────
  const handleRetainerSelect = (retainer) => {
    setSelectedRetainer(retainer.id);

    // Reset to defaults when switching between retainer types
    const services = RETAINERS_WITH_SERVICES.has(retainer.id)
      ? (selectedRetainer === retainer.id ? selectedRetainerServices : RETAINER_SERVICE_DEFAULTS)
      : [];

    if (selectedRetainer !== retainer.id && RETAINERS_WITH_SERVICES.has(retainer.id)) {
      setSelectedRetainerServices(RETAINER_SERVICE_DEFAULTS);
    }

    onSelectEngagementType('retainer', retainer.id, retainer.name, [], services);
  };

  // ── Retainer services: toggle ─────────────────────────────────────
  const toggleRetainerService = (service) => {
    const updated = selectedRetainerServices.includes(service)
      ? selectedRetainerServices.filter((s) => s !== service)
      : [...selectedRetainerServices, service];

    setSelectedRetainerServices(updated);
    onSelectEngagementType(
      'retainer',
      selectedRetainer,
      RETAINER_TYPES.find((r) => r.id === selectedRetainer)?.name || '',
      [],
      updated
    );
  };

  // ── Projects: card toggle ─────────────────────────────────────────
  const toggleProject = (project) => {
    const updated = selectedProjects.some((p) => p.id === project.id)
      ? selectedProjects.filter((p) => p.id !== project.id)
      : [...selectedProjects, project];

    setSelectedProjects(updated);

    onSelectEngagementType(
      'project',
      updated.map((p) => p.id),
      updated.map((p) => p.name).join(', '),
      updated
    );
  };

  const showRetainerServices = selectedRetainer && RETAINERS_WITH_SERVICES.has(selectedRetainer);

  return (
    <div className="space-y-6">

      {/* ── Page title ─────────────────────────────────────────────── */}
      <div>
        <h2 className="text-lg font-semibold text-gray-900">Engagement Type</h2>
        <p className="text-sm text-gray-500 mt-0.5">
          Select the type of work you are bidding for.
        </p>
      </div>

      {/* ── Top-level engagement type cards ───────────────────────── */}
      <div className="grid grid-cols-2 gap-3">
        {ENGAGEMENT_TYPES.map(({ id, label, icon: Icon, description, color }) => {
          const active = engagementType === id;
          const ispurple = color === 'purple';
          return (
            <button
              key={id}
              data-tour={`step1-${id}-card`}
              onClick={() => handleTypeToggle(id)}
              className={`relative flex flex-col items-start gap-2 p-4 rounded-xl border-2 text-left transition-all duration-150
                ${active
                  ? ispurple
                    ? 'border-purple-500 bg-purple-50 ring-1 ring-purple-500'
                    : 'border-purple-500 bg-purple-50 ring-1 ring-purple-500'
                  : 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50'
                }`}
            >
              {active && (
                <CheckCircleSolidIcon
                  className={`absolute top-3 right-3 w-5 h-5 ${ispurple ? 'text-purple-600' : 'text-purple-600'}`}
                />
              )}

              <div className={`p-2 rounded-lg ${
                active
                  ? ispurple ? 'bg-purple-100' : 'bg-purple-100'
                  : 'bg-gray-100'
              }`}>
                <Icon className={`w-5 h-5 ${
                  active
                    ? ispurple ? 'text-purple-600' : 'text-purple-600'
                    : 'text-gray-500'
                }`} />
              </div>

              <div>
                <div className={`text-sm font-semibold ${
                  active
                    ? ispurple ? 'text-purple-900' : 'text-purple-900'
                    : 'text-gray-900'
                }`}>
                  {label}
                </div>
                <div className="text-xs text-gray-500 mt-0.5 leading-snug">
                  {description}
                </div>
              </div>
            </button>
          );
        })}
      </div>

      {/* ── Sub-options ────────────────────────────────────────────── */}

      {/* RETAINER — type selection */}
      {engagementType === 'retainer' && (
        <div className="space-y-3">
          <p className="text-xs font-medium text-gray-400 uppercase tracking-wide">
            Select a retainer type
          </p>
          <div className="grid grid-cols-3 gap-2">
            {RETAINER_TYPES.map((retainer) => {
              const selected = selectedRetainer === retainer.id;
              return (
                <button
                  key={retainer.id}
                  data-tour={`step1-${retainer.id}`}
                  onClick={() => handleRetainerSelect(retainer)}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg border-2 text-left transition-all duration-150
                    ${selected
                      ? 'border-purple-500 bg-purple-50 ring-1 ring-purple-500'
                      : 'border-gray-200 bg-white hover:border-purple-300 hover:bg-purple-50/40'
                    }`}
                >
                  <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center shrink-0
                    ${selected ? 'border-purple-600' : 'border-gray-300'}`}
                  >
                    {selected && <div className="w-2 h-2 rounded-full bg-purple-600" />}
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className={`text-sm font-semibold ${selected ? 'text-purple-900' : 'text-gray-900'}`}>
                      {retainer.name}
                    </div>
                    <div className="text-xs text-gray-500 mt-0.5">
                      {RETAINER_DESCRIPTIONS[retainer.id] || ''}
                    </div>
                  </div>

                  {selected && (
                    <CheckCircleSolidIcon className="w-5 h-5 text-purple-600 shrink-0" />
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* RETAINER A / B — service selector */}
      {engagementType === 'retainer' && showRetainerServices && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <p className="text-xs font-medium text-gray-400 uppercase tracking-wide">
              Included services
            </p>
            {selectedRetainerServices.length > 0 && (
              <span className="text-xs font-semibold text-purple-700 bg-purple-50 border border-purple-200 rounded-full px-2.5 py-0.5">
                {selectedRetainerServices.length} selected
              </span>
            )}
          </div>

          <div className="grid grid-cols-3 gap-2">
            {RETAINER_SERVICE_OPTIONS.map((service) => {
              const selected = selectedRetainerServices.includes(service);
              const isDefault = RETAINER_SERVICE_DEFAULTS.includes(service);
              return (
                <button
                  key={service}
                  onClick={() => toggleRetainerService(service)}
                  className={`relative flex items-start px-3 py-2.5 rounded-lg border-2 text-left transition-all duration-100
                    ${selected
                      ? 'border-purple-500 bg-purple-50 ring-1 ring-purple-500'
                      : 'border-gray-200 bg-white hover:border-purple-300 hover:bg-purple-50/30'
                    }`}
                >
                  {selected && (
                    <CheckCircleSolidIcon className="absolute top-2 right-2 w-3.5 h-3.5 text-purple-600 shrink-0" />
                  )}
                  <div className="pr-4">
                    <span className={`text-xs font-medium leading-snug ${
                      selected ? 'text-purple-900' : 'text-gray-700'
                    }`}>
                      {service}
                    </span>
                    {isDefault && (
                      <span className="block text-xs text-purple-400 mt-0.5">Default</span>
                    )}
                  </div>
                </button>
              );
            })}
          </div>

          {selectedRetainerServices.length === 0 && (
            <p className="text-xs text-gray-400">
              Select at least one service to continue.
            </p>
          )}
        </div>
      )}

      {/* PROJECT — small cards grid */}
      {engagementType === 'project' && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <p className="text-xs font-medium text-gray-400 uppercase tracking-wide">
              Select project types
            </p>
            {selectedProjects.length > 0 && (
              <span className="text-xs font-semibold text-purple-700 bg-purple-50 border border-purple-200 rounded-full px-2.5 py-0.5">
                {selectedProjects.length} selected
              </span>
            )}
          </div>

          <div className="grid grid-cols-3 gap-2">
            {PROJECT_TYPES.map((project) => {
              const selected = selectedProjects.some((p) => p.id === project.id);
              return (
                <button
                  key={project.id}
                  onClick={() => toggleProject(project)}
                  className={`relative flex items-start px-3 py-2.5 rounded-lg border-2 text-left transition-all duration-100
                    ${selected
                      ? 'border-purple-500 bg-purple-50 ring-1 ring-purple-500'
                      : 'border-gray-200 bg-white hover:border-purple-300 hover:bg-purple-50/30'
                    }`}
                >
                  {selected && (
                    <CheckCircleSolidIcon className="absolute top-2 right-2 w-3.5 h-3.5 text-purple-600 shrink-0" />
                  )}
                  <span className={`text-xs font-medium leading-snug pr-4 ${
                    selected ? 'text-purple-900' : 'text-gray-700'
                  }`}>
                    {project.name}
                  </span>
                </button>
              );
            })}
          </div>

          {selectedProjects.length === 0 && (
            <p className="text-xs text-gray-400">
              Select one or more — use the Continue button below when ready.
            </p>
          )}

          {selectedProjects.length > 0 && (
            <p className="text-xs text-gray-500">
              <span className="font-medium">Selected:</span>{' '}
              {selectedProjects.map((p) => p.name).join(' · ')}
            </p>
          )}
        </div>
      )}

      {/* ── Empty state ─────────────────────────────────────────────── */}
      {!engagementType && (
        <div className="rounded-lg border border-dashed border-gray-200 bg-gray-50 py-10 text-center">
          <p className="text-sm text-gray-400">Choose Retainer or Project above to continue.</p>
        </div>
      )}
    </div>
  );
};

export default Step1EngagementType;